import React from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

/**
 * CashFlowChart - Interactive timeline showing cash inflows and outflows
 */
const CashFlowChart = ({ purchaseTransactions, saleTransactions, additionalTransactions }) => {
    // Prepare data for the chart
    const prepareChartData = () => {
        const allTransactions = [];

        // Add purchase transactions (negative/outflow)
        purchaseTransactions?.forEach(t => {
            allTransactions.push({
                date: new Date(t.transaction_date || t.date),
                dateStr: t.transaction_date || t.date,
                purchase: -Math.abs(parseFloat(t.amount)),
                type: 'Purchase',
                description: t.description || t.category
            });
        });

        // Add sale transactions (positive/inflow)
        saleTransactions?.forEach(t => {
            allTransactions.push({
                date: new Date(t.transaction_date || t.date),
                dateStr: t.transaction_date || t.date,
                sale: parseFloat(t.amount),
                type: 'Sale',
                description: t.description || t.category
            });
        });

        // Add additional costs (negative/outflow)
        additionalTransactions?.forEach(t => {
            allTransactions.push({
                date: new Date(t.transaction_date || t.date),
                dateStr: t.transaction_date || t.date,
                cost: -Math.abs(parseFloat(t.amount)),
                type: 'Cost',
                description: t.description || t.category
            });
        });

        // Sort by date
        allTransactions.sort((a, b) => a.date - b.date);

        // Calculate cumulative cash flow
        let cumulative = 0;
        const chartData = allTransactions.map(t => {
            const netFlow = (t.purchase || 0) + (t.sale || 0) + (t.cost || 0);
            cumulative += netFlow;

            return {
                date: t.dateStr,
                displayDate: new Date(t.dateStr).toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                }),
                purchase: t.purchase || 0,
                sale: t.sale || 0,
                cost: t.cost || 0,
                cumulative: cumulative,
                type: t.type,
                description: t.description
            };
        });

        return chartData;
    };

    const data = prepareChartData();

    const formatCurrency = (value) => {
        return `₹${Math.abs(value).toLocaleString('en-IN')}`;
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{data.displayDate}</p>
                    <p style={{ margin: '4px 0', fontSize: '0.9rem' }}>{data.description}</p>
                    {data.purchase !== 0 && (
                        <p style={{ margin: '4px 0', color: '#ef4444' }}>
                            Purchase: {formatCurrency(data.purchase)}
                        </p>
                    )}
                    {data.sale !== 0 && (
                        <p style={{ margin: '4px 0', color: '#10b981' }}>
                            Sale: {formatCurrency(data.sale)}
                        </p>
                    )}
                    {data.cost !== 0 && (
                        <p style={{ margin: '4px 0', color: '#f59e0b' }}>
                            Cost: {formatCurrency(data.cost)}
                        </p>
                    )}
                    <p style={{ margin: '8px 0 0 0', fontWeight: 'bold', borderTop: '1px solid #eee', paddingTop: '8px' }}>
                        Cumulative: {formatCurrency(data.cumulative)}
                    </p>
                </div>
            );
        }
        return null;
    };

    if (!data || data.length === 0) {
        return (
            <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#666',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
            }}>
                <p style={{ fontSize: '1.1rem', marginBottom: '8px' }}>📊 No transaction data available</p>
                <p style={{ fontSize: '0.9rem' }}>Add transactions to see your cash flow timeline</p>
            </div>
        );
    }

    return (
        <div>
            <h3 style={{ marginBottom: '16px', color: '#1a1a2e' }}>💰 Cash Flow Timeline</h3>
            <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={data}>
                    <defs>
                        <linearGradient id="cumulativeGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#667eea" stopOpacity={0.1} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                        dataKey="displayDate"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                    />
                    <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={formatCurrency}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                        type="monotone"
                        dataKey="cumulative"
                        stroke="#667eea"
                        fill="url(#cumulativeGradient)"
                        name="Cumulative Cash Flow"
                    />
                    <Line
                        type="monotone"
                        dataKey="purchase"
                        stroke="#ef4444"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        name="Purchase"
                    />
                    <Line
                        type="monotone"
                        dataKey="sale"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        name="Sale"
                    />
                    <Line
                        type="monotone"
                        dataKey="cost"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        name="Additional Costs"
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default CashFlowChart;
